/**
 * API Client - Axios instance with interceptors
 */

import axios from 'axios'
import type { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { API_CONFIG } from '@/config/constants'

/**
 * Create axios instance with default configuration
 */
export const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Request interceptor
 * Add request ID for tracing
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add request timestamp for logging
    config.headers['X-Request-Time'] = new Date().toISOString()

    return config
  },
  (error: AxiosError) => {
    console.error('[API] Request error:', error)
    return Promise.reject(error)
  }
)

/**
 * Response interceptor
 * Handle common errors and logging
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: AxiosError) => {
    // Log error
    console.error('[API] Response error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
    })

    // Handle specific status codes
    if (error.response?.status === 500) {
      console.error('[API] Server error:', error.response.data)
    } else if (error.code === 'ECONNABORTED') {
      console.error('[API] Request timeout')
    } else if (error.code === 'ERR_NETWORK') {
      console.error('[API] Network error - backend may be down')
    }

    return Promise.reject(error)
  }
)
